import React, { useEffect, useMemo, useRef, useState } from "react";
import $ from "jquery";
import ApiURl from "../controllers/Api";
import imgLocation from "../controllers/imagePath";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Heart,
  ShoppingCart,
  Trash2,
  Truck,
  RefreshCcw,
  ShieldCheck,
  LockKeyhole,
  ArrowRight,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const FALLBACK_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="500" height="500">
      <rect width="100%" height="100%" fill="#f7eee4"/>
      <text
        x="50%"
        y="50%"
        dominant-baseline="middle"
        text-anchor="middle"
        font-family="Arial"
        font-size="22"
        fill="#806f68"
      >
        No Image
      </text>
    </svg>
  `);

const Wishlist = () => {
  const navigate = useNavigate();

  const pageRef = useRef(null);
  const headerRef = useRef(null);
  const cardsWrapperRef = useRef(null);
  const benefitsRef = useRef(null);
  const cardRefs = useRef([]);

  const [wishlistItems, setWishlistItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState("");
  const [removingId, setRemovingId] = useState(null);

  const user_id = localStorage.getItem("user_id");

  const showToast = (message) => {
    setToast(message);

    window.setTimeout(() => {
      setToast("");
    }, 2500);
  };

  useEffect(() => {
    const fetchWishlistItems = async () => {
      setIsLoading(true);

      try {
        const response = await $.getJSON(
          `${ApiURl}/getWishlistItems.php?user_id=${user_id}`
        );

        setWishlistItems(response?.wishlist || []);
      } catch (error) {
        console.error("Error fetching wishlist items:", error);
        showToast("Failed to load wishlist");
      } finally {
        setIsLoading(false);
      }
    };

    if (user_id) {
      fetchWishlistItems();
    }
  }, [user_id]);

  const removeItem = async (productId) => {
    if (removingId) return;

    setRemovingId(productId);

    try {
      await $.ajax({
        url: `${ApiURl}/removeWishlistItem.php`,
        method: "POST",
        contentType: "application/json",
        data: JSON.stringify({
          user_id,
          product_id: productId,
        }),
      });

      setWishlistItems((previousItems) =>
        previousItems.filter(
          (item) => String(item.product_id) !== String(productId)
        )
      );

      window.dispatchEvent(
        new CustomEvent("wishlist:changed", {
          detail: {
            productId,
            inWishlist: false,
          },
        })
      );

      showToast("Removed from wishlist");
    } catch (error) {
      console.error("Error removing item from wishlist:", error);
      showToast("Failed to remove item");
    } finally {
      setRemovingId(null);
    }
  };

  const hasItems = useMemo(
    () => wishlistItems.length > 0,
    [wishlistItems]
  );

  const joinUrl = (base, path) => {
    if (!path || typeof path !== "string") return "";

    if (
      path.startsWith("http://") ||
      path.startsWith("https://") ||
      path.startsWith("data:")
    ) {
      return path;
    }

    const cleanBase = String(base || "").replace(/\/+$/, "");
    const cleanPath = path.replace(/^\/+/, "");

    return cleanBase ? `${cleanBase}/${cleanPath}` : `/${cleanPath}`;
  };

  const getWishlistImage = (item) => {
    if (
      item?.images &&
      Array.isArray(item.images) &&
      item.images.length > 0
    ) {
      const firstImage = item.images[0];
      const source =
        firstImage?.url ||
        firstImage?.filename ||
        firstImage?.image_url ||
        firstImage;

      return joinUrl(imgLocation, source) || FALLBACK_IMG;
    }

    if (item?.image) {
      return joinUrl(imgLocation, item.image) || FALLBACK_IMG;
    }

    const raw = item?.img_path || item?.image_path;

    if (!raw) return FALLBACK_IMG;

    if (
      typeof raw === "string" &&
      (raw.startsWith("http://") ||
        raw.startsWith("https://") ||
        raw.startsWith("data:"))
    ) {
      return raw.trim();
    }

    if (typeof raw === "string") {
      const cleaned = raw.trim();

      try {
        const parsed = JSON.parse(cleaned);

        if (Array.isArray(parsed) && parsed.length > 0) {
          const first = String(parsed[0] || "").trim();
          return joinUrl(imgLocation, first) || FALLBACK_IMG;
        }
      } catch (error) {
        // Continue with comma-separated or single path.
      }

      const parts = cleaned
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);

      if (parts.length > 0) {
        return joinUrl(imgLocation, parts[0]) || FALLBACK_IMG;
      }

      return joinUrl(imgLocation, cleaned) || FALLBACK_IMG;
    }

    return FALLBACK_IMG;
  };

  const getProductName = (item) =>
    item?.name ||
    item?.product_name ||
    item?.productName ||
    "Gold Jewellery";

  const getProductPrice = (item) => {
    const discountedPrice = Number(
      item?.discounted_price || item?.discount_price || 0
    );

    const regularPrice = Number(
      item?.min_price ||
        item?.price ||
        item?.product_price ||
        item?.original_price ||
        0
    );

    const finalPrice =
      discountedPrice > 0 &&
      regularPrice > 0 &&
      discountedPrice < regularPrice
        ? discountedPrice
        : regularPrice;

    if (!finalPrice || Number.isNaN(finalPrice)) {
      return "Price on request";
    }

    return `₹${Math.round(finalPrice).toLocaleString("en-IN")}`;
  };

  const getSize = (item) => {
    const size =
      item?.size ||
      item?.product_size ||
      item?.sizes ||
      item?.selected_size;

    if (!size) return "N/A";

    if (typeof size === "string" && size.includes(",")) {
      return size.split(",")[0].trim();
    }

    return size;
  };

  const getMetal = (item) =>
    item?.metal ||
    item?.metal_name ||
    item?.material ||
    item?.purity ||
    "18K Yellow Gold";

  useEffect(() => {
    if (!pageRef.current) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reduceMotion) {
      gsap.set(
        [
          headerRef.current,
          cardsWrapperRef.current,
          benefitsRef.current,
          ...cardRefs.current.filter(Boolean),
        ],
        {
          opacity: 1,
          x: 0,
          y: 0,
          scale: 1,
        }
      );

      return;
    }

    const context = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        {
          opacity: 0,
          y: 35,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
        }
      );

      const cards = cardRefs.current.filter(Boolean);

      if (cards.length) {
        ScrollTrigger.batch(cards, {
          start: "top 90%",
          once: true,
          onEnter: (elements) => {
            gsap.fromTo(
              elements,
              {
                opacity: 0,
                y: 45,
                scale: 0.97,
              },
              {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.75,
                stagger: 0.09,
                ease: "power3.out",
              }
            );
          },
        });
      }

      if (benefitsRef.current) {
        gsap.fromTo(
          benefitsRef.current,
          {
            opacity: 0,
            y: 35,
          },
          {
            opacity: 1,
            y: 0,
            duration: 0.85,
            ease: "power3.out",
            scrollTrigger: {
              trigger: benefitsRef.current,
              start: "top 91%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }
    }, pageRef);

    const refreshTimeout = window.setTimeout(() => {
      ScrollTrigger.refresh();
    }, 400);

    return () => {
      window.clearTimeout(refreshTimeout);
      context.revert();
    };
  }, [wishlistItems.length, isLoading]);

  const goToProduct = (productId) => {
    navigate(`/ProductOverview/${productId}`);
  };

  const handleMoveAllToCart = () => {
    if (!wishlistItems.length) return;

    showToast("Open a product to select its size and add it to cart");
  };

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.45 }}
      className="wishlist-empty-state"
    >
      <div className="wishlist-empty-icon">
        <Heart />
      </div>

      <h2>Your wishlist is empty</h2>

      <p>
        Save your favourite jewellery pieces and find them easily whenever
        you return.
      </p>

      <Link to="/all-products" className="wishlist-primary-link">
        Explore Collection
        <ArrowRight />
      </Link>
    </motion.div>
  );

  const LoginPrompt = () => (
    <div className="wishlist-empty-state">
      <div className="wishlist-empty-icon">
        <Heart />
      </div>

      <h2>Login Required</h2>

      <p>Please log in to view and manage your saved jewellery.</p>

      <Link to="/login" className="wishlist-primary-link">
        Go to Login
        <ArrowRight />
      </Link>
    </div>
  );

  const LoadingState = () => (
    <div className="wishlist-loading-state">
      <div className="wishlist-spinner" />
      <p>Loading your wishlist...</p>
    </div>
  );

  const BenefitItem = ({ icon: Icon, title, description }) => (
    <div className="wishlist-benefit">
      <div className="wishlist-benefit-icon">
        <Icon />
      </div>

      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    </div>
  );

  return (
    <section
      ref={pageRef}
      className="wishlist-page"
      style={{ backgroundColor: "var(--user-bg, #E8D9D5)" }}
    >
      <div className="wishlist-container">
        <header ref={headerRef} className="wishlist-header">
          <div className="wishlist-heading-copy">
            <h1>My Wishlist</h1>

            <div className="wishlist-title-divider">
              <span />
              <i />
              <span />
            </div>

            <p>
              {wishlistItems.length}{" "}
              {wishlistItems.length === 1 ? "item" : "items"} saved in your
              wishlist
            </p>
          </div>

          {hasItems && (
            <button
              type="button"
              onClick={handleMoveAllToCart}
              className="wishlist-move-all"
            >
              <Heart />
              Move All to Cart
            </button>
          )}
        </header>

        {isLoading ? (
          <LoadingState />
        ) : !user_id ? (
          <LoginPrompt />
        ) : hasItems ? (
          <>
            <div
              ref={cardsWrapperRef}
              className="wishlist-products"
            >
              <AnimatePresence mode="popLayout">
                {wishlistItems.map((item, index) => {
                  const imageUrl = getWishlistImage(item);
                  const name = getProductName(item);
                  const price = getProductPrice(item);
                  const size = getSize(item);
                  const metal = getMetal(item);

                  return (
                    <motion.article
                      layout
                      key={item.product_id}
                      ref={(element) => {
                        cardRefs.current[index] = element;
                      }}
                      initial={{
                        opacity: 0,
                        y: 25,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        scale: 0.94,
                      }}
                      transition={{
                        duration: 0.35,
                      }}
                      className="wishlist-card"
                    >
                      <Link
                        to={`/ProductOverview/${item.product_id}`}
                        className="wishlist-image-link"
                        aria-label={`View ${name}`}
                      >
                        <div className="wishlist-image-wrapper">
                          <img
                            src={imageUrl}
                            alt={name}
                            loading="lazy"
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = FALLBACK_IMG;
                            }}
                          />
                        </div>
                      </Link>

                      <button
                        type="button"
                        className="wishlist-heart-button"
                        onClick={() => removeItem(item.product_id)}
                        disabled={removingId === item.product_id}
                        aria-label={`Remove ${name} from wishlist`}
                      >
                        <Heart fill="currentColor" />
                      </button>

                      <div className="wishlist-card-content">
                        <div className="wishlist-card-main">
                          <Link
                            to={`/ProductOverview/${item.product_id}`}
                            className="wishlist-product-name"
                          >
                            {name}
                          </Link>

                          <p className="wishlist-metal">{metal}</p>

                          <p className="wishlist-size">
                            Size: {size}
                          </p>

                          <p className="wishlist-price">{price}</p>
                        </div>

                        <div className="wishlist-card-actions">
                          <button
                            type="button"
                            className="wishlist-cart-button"
                            onClick={() =>
                              goToProduct(item.product_id)
                            }
                          >
                            <ShoppingCart />
                            <span>Add to Cart</span>
                          </button>

                          <button
                            type="button"
                            className="wishlist-remove-button"
                            onClick={() => removeItem(item.product_id)}
                            disabled={removingId === item.product_id}
                            aria-label={`Delete ${name}`}
                          >
                            {removingId === item.product_id ? (
                              <span className="wishlist-small-spinner" />
                            ) : (
                              <Trash2 />
                            )}
                          </button>
                        </div>
                      </div>
                    </motion.article>
                  );
                })}
              </AnimatePresence>
            </div>

            <div ref={benefitsRef} className="wishlist-benefits">
              <BenefitItem
                icon={Truck}
                title="Free Shipping"
                description="On orders above ₹20,000"
              />

              <BenefitItem
                icon={RefreshCcw}
                title="Easy Returns"
                description="7 days return policy"
              />

              <BenefitItem
                icon={ShieldCheck}
                title="Certified Jewellery"
                description="100% Hallmarked"
              />

              <BenefitItem
                icon={LockKeyhole}
                title="Secure Payments"
                description="100% Safe & Secure"
              />
            </div>
          </>
        ) : (
          <EmptyState />
        )}
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{
              opacity: 0,
              y: 24,
              scale: 0.96,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
            }}
            exit={{
              opacity: 0,
              y: 18,
              scale: 0.96,
            }}
            className="wishlist-toast"
          >
            <Heart fill="currentColor" />
            <span>{toast}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .wishlist-page,
        .wishlist-page * {
          box-sizing: border-box;
        }

        .wishlist-page {
          --wishlist-maroon: var(--user-maroon, #2b0000);
          --wishlist-maroon-dark: var(--user-maroon-deep, #190000);
          --wishlist-gold: var(--user-gold, #bd7c16);
          --wishlist-gold-light: var(--user-gold-soft, #d9a84d);
          --wishlist-cream: var(--user-cream, #fffaf5);
          --wishlist-paper: var(--user-card, #fbf5ef);
          --wishlist-line: var(--user-border, #ead8ca);
          --wishlist-text: var(--user-text, #2b1715);
          --wishlist-muted: var(--user-text-soft, #665952);

          width: 100%;
          min-height: 100vh;
          padding: 35px 20px 48px;
          overflow-x: hidden;
          color: var(--wishlist-text);
          font-family: "Kanit", Arial, Helvetica, sans-serif;
        }

        .wishlist-container {
          width: min(1500px, calc(100% - 72px));
          margin: 0 auto;
        }

        .wishlist-header {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 30px;
          margin-bottom: 34px;
        }

        .wishlist-heading-copy h1 {
          margin: 0;
          color: var(--wishlist-maroon);
          font-family: Georgia, "Times New Roman", serif;
          font-size: clamp(43px, 4vw, 61px);
          font-weight: 500;
          line-height: 1;
          letter-spacing: -0.025em;
        }

        .wishlist-title-divider {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 185px;
          margin: 15px 0 13px;
          color: var(--wishlist-gold);
        }

        .wishlist-title-divider span {
          flex: 1;
          height: 1px;
          background: currentColor;
        }

        .wishlist-title-divider i {
          display: block;
          width: 9px;
          height: 9px;
          border: 1px solid currentColor;
          transform: rotate(45deg);
        }

        .wishlist-heading-copy p {
          margin: 0;
          color: #261d1a;
          font-size: 16px;
          line-height: 1.5;
        }

        .wishlist-move-all {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          min-height: 52px;
          padding: 12px 29px;
          border: 1px solid var(--wishlist-gold);
          border-radius: 9px;
          color: #9d5800;
          background: rgba(255, 250, 245, 0.48);
          font-family: inherit;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition:
            color 250ms ease,
            background 250ms ease,
            transform 250ms ease,
            box-shadow 250ms ease;
        }

        .wishlist-move-all svg {
          width: 19px;
          height: 19px;
          stroke-width: 1.8;
        }

        .wishlist-move-all:hover {
          color: white;
          background: var(--wishlist-maroon);
          transform: translateY(-3px);
          box-shadow: 0 13px 24px rgba(43, 0, 0, 0.15);
        }

        .wishlist-products {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 18px;
          align-items: start;
        }

        .wishlist-card {
          position: relative;
          display: flex;
          min-width: 0;
          flex-direction: column;
          overflow: hidden;
          border: 1px solid var(--wishlist-line);
          border-radius: 10px;
          background: rgba(255, 250, 246, 0.84);
          box-shadow: 0 5px 18px rgba(74, 28, 20, 0.04);
          transition:
            transform 350ms ease,
            box-shadow 350ms ease,
            border-color 350ms ease;
        }

        .wishlist-card:hover {
          border-color: rgba(201, 155, 63, 0.8);
          transform: translateY(-5px);
          box-shadow: 0 18px 40px rgba(62, 17, 11, 0.12);
        }

        .wishlist-image-link {
          display: block;
          color: inherit;
          text-decoration: none;
        }

        .wishlist-image-wrapper {
          position: relative;
          width: 100%;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          background: #f9f0e7;
        }

        .wishlist-image-wrapper::after {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            linear-gradient(
              0deg,
              rgba(90, 46, 20, 0.04),
              transparent 45%
            ),
            radial-gradient(
              circle at center,
              transparent 55%,
              rgba(110, 72, 42, 0.04)
            );
        }

        .wishlist-image-wrapper img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          transform: scale(1);
          transition: transform 700ms cubic-bezier(0.22, 1, 0.36, 1);
        }

        .wishlist-card:hover .wishlist-image-wrapper img {
          transform: scale(1.06);
        }

        .wishlist-heart-button {
          position: absolute;
          top: 13px;
          right: 13px;
          z-index: 4;
          display: grid;
          width: 38px;
          height: 38px;
          place-items: center;
          border: 0;
          border-radius: 50%;
          color: var(--wishlist-maroon);
          background: rgba(255, 255, 255, 0.94);
          box-shadow: 0 7px 17px rgba(43, 0, 0, 0.08);
          cursor: pointer;
          transition:
            color 220ms ease,
            background 220ms ease,
            transform 220ms ease;
        }

        .wishlist-heart-button svg {
          width: 18px;
          height: 18px;
          stroke-width: 1.7;
        }

        .wishlist-heart-button:hover {
          color: #a65f00;
          background: #fff8ec;
          transform: scale(1.08);
        }

        .wishlist-heart-button:disabled {
          cursor: wait;
          opacity: 0.55;
        }

        .wishlist-card-content {
          display: flex;
          flex: 1;
          flex-direction: column;
          padding: 12px 14px 13px;
        }

        .wishlist-card-main {
          flex: 1;
        }

        .wishlist-product-name {
          display: -webkit-box;
          overflow: hidden;
          margin: 0;
          color: var(--wishlist-maroon);
          font-family: Georgia, "Times New Roman", serif;
          font-size: 18px;
          font-weight: 500;
          line-height: 1.2;
          text-decoration: none;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          transition: color 200ms ease;
        }

        .wishlist-product-name:hover {
          color: var(--wishlist-gold);
        }

        .wishlist-metal,
        .wishlist-size {
          margin: 6px 0 0;
          color: #684d47;
          font-size: 12px;
          line-height: 1.4;
        }

        .wishlist-size {
          margin-top: 6px;
          color: var(--wishlist-muted);
        }

        .wishlist-price {
          margin: 7px 0 0;
          color: var(--wishlist-maroon-dark);
          font-size: 16px;
          font-weight: 700;
          line-height: 1.2;
        }

        .wishlist-card-actions {
          display: grid;
          grid-template-columns: 1fr 48px;
          gap: 10px;
          margin-top: auto;
          padding-top: 10px;
        }

        .wishlist-cart-button,
        .wishlist-remove-button {
          min-height: 45px;
          border: 1px solid var(--wishlist-line);
          border-radius: 8px;
          font-family: inherit;
          cursor: pointer;
          transition:
            color 220ms ease,
            background 220ms ease,
            border-color 220ms ease,
            transform 220ms ease;
        }

        .wishlist-cart-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          color: var(--wishlist-maroon);
          background: linear-gradient(135deg, #f6eadc, #fff8ee);
          font-size: 14px;
          font-weight: 500;
        }

        .wishlist-cart-button svg {
          width: 18px;
          height: 18px;
          color: var(--wishlist-gold);
          stroke-width: 1.7;
        }

        .wishlist-cart-button:hover {
          border-color: var(--wishlist-maroon);
          color: white;
          background: var(--wishlist-maroon);
          transform: translateY(-2px);
        }

        .wishlist-cart-button:hover svg {
          color: var(--wishlist-gold-light);
        }

        .wishlist-remove-button {
          display: grid;
          place-items: center;
          color: var(--wishlist-maroon);
          background: rgba(255, 255, 255, 0.7);
        }

        .wishlist-remove-button svg {
          width: 18px;
          height: 18px;
          stroke-width: 1.6;
        }

        .wishlist-remove-button:hover {
          border-color: #9d201d;
          color: white;
          background: #7e1513;
          transform: translateY(-2px);
        }

        .wishlist-remove-button:disabled {
          cursor: wait;
          opacity: 0.6;
        }

        .wishlist-small-spinner {
          display: block;
          width: 17px;
          height: 17px;
          border: 2px solid rgba(43, 0, 0, 0.2);
          border-top-color: var(--wishlist-maroon);
          border-radius: 50%;
          animation: wishlistSpin 0.75s linear infinite;
        }

        .wishlist-benefits {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          margin-top: 38px;
          padding: 28px 22px;
          border: 1px solid var(--wishlist-line);
          border-radius: 12px;
          background: rgba(255, 250, 245, 0.55);
          box-shadow: 0 12px 28px rgba(43, 0, 0, 0.035);
        }

        .wishlist-benefit {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 17px;
          min-height: 65px;
          padding: 0 22px;
        }

        .wishlist-benefit:not(:last-child) {
          border-right: 1px solid var(--wishlist-line);
        }

        .wishlist-benefit-icon {
          display: grid;
          width: 47px;
          height: 47px;
          flex: none;
          place-items: center;
          color: var(--wishlist-gold);
        }

        .wishlist-benefit-icon svg {
          width: 31px;
          height: 31px;
          stroke-width: 1.5;
        }

        .wishlist-benefit h3 {
          margin: 0;
          color: var(--wishlist-maroon);
          font-size: 14px;
          font-weight: 600;
          line-height: 1.2;
        }

        .wishlist-benefit p {
          margin: 6px 0 0;
          color: var(--wishlist-muted);
          font-size: 12px;
          line-height: 1.35;
        }

        .wishlist-empty-state,
        .wishlist-loading-state {
          display: flex;
          min-height: 420px;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 50px 25px;
          border: 1px solid var(--wishlist-line);
          border-radius: 16px;
          text-align: center;
          background: rgba(255, 250, 245, 0.7);
          box-shadow: 0 20px 45px rgba(43, 0, 0, 0.07);
        }

        .wishlist-empty-icon {
          display: grid;
          width: 86px;
          height: 86px;
          margin-bottom: 22px;
          place-items: center;
          border: 1px solid rgba(189, 124, 22, 0.38);
          border-radius: 50%;
          color: var(--wishlist-gold);
          background: linear-gradient(135deg, #fffaf5, #f4e5d3);
        }

        .wishlist-empty-icon svg {
          width: 42px;
          height: 42px;
          stroke-width: 1.5;
        }

        .wishlist-empty-state h2 {
          margin: 0;
          color: var(--wishlist-maroon);
          font-family: Georgia, "Times New Roman", serif;
          font-size: 34px;
          font-weight: 500;
        }

        .wishlist-empty-state p {
          max-width: 520px;
          margin: 12px auto 0;
          color: var(--wishlist-muted);
          font-size: 15px;
          line-height: 1.6;
        }

        .wishlist-primary-link {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-top: 25px;
          min-height: 48px;
          padding: 12px 25px;
          border-radius: 7px;
          color: white;
          background: var(--wishlist-maroon);
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
          transition:
            transform 220ms ease,
            box-shadow 220ms ease;
        }

        .wishlist-primary-link svg {
          width: 18px;
          height: 18px;
        }

        .wishlist-primary-link:hover {
          transform: translateY(-3px);
          box-shadow: 0 13px 25px rgba(43, 0, 0, 0.18);
        }

        .wishlist-loading-state p {
          margin: 18px 0 0;
          color: var(--wishlist-muted);
          font-size: 14px;
        }

        .wishlist-spinner {
          width: 52px;
          height: 52px;
          border: 4px solid rgba(189, 124, 22, 0.18);
          border-top-color: var(--wishlist-gold);
          border-radius: 50%;
          animation: wishlistSpin 0.8s linear infinite;
        }

        .wishlist-toast {
          position: fixed;
          right: 22px;
          bottom: 22px;
          z-index: 1000;
          display: flex;
          align-items: center;
          gap: 11px;
          max-width: min(380px, calc(100vw - 30px));
          padding: 13px 18px;
          border: 1px solid rgba(217, 168, 77, 0.35);
          border-radius: 10px;
          color: #fff8ed;
          background:
            radial-gradient(
              circle at 20% 10%,
              rgba(104, 0, 8, 0.8),
              transparent 50%
            ),
            var(--wishlist-maroon);
          box-shadow: 0 16px 34px rgba(43, 0, 0, 0.28);
          font-size: 14px;
          font-weight: 500;
        }

        .wishlist-toast svg {
          width: 18px;
          height: 18px;
          color: var(--wishlist-gold-light);
        }

        @keyframes wishlistSpin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 1100px) {
          .wishlist-container {
            width: min(100% - 34px, 1100px);
          }

          .wishlist-products {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }

          .wishlist-benefit {
            padding-inline: 12px;
          }
        }

        @media (max-width: 820px) {
          .wishlist-products {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .wishlist-benefits {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            padding: 12px;
          }

          .wishlist-benefit {
            min-height: 100px;
            justify-content: flex-start;
            padding: 18px;
          }

          .wishlist-benefit:not(:last-child) {
            border-right: 0;
          }

          .wishlist-benefit:nth-child(odd) {
            border-right: 1px solid var(--wishlist-line);
          }

          .wishlist-benefit:nth-child(-n + 2) {
            border-bottom: 1px solid var(--wishlist-line);
          }
        }

        @media (max-width: 640px) {
          .wishlist-page {
            padding: 25px 12px 32px;
          }

          .wishlist-header {
            display: block;
            margin-bottom: 22px;
          }

          .wishlist-heading-copy h1 {
            font-size: 38px;
          }

          .wishlist-title-divider {
            width: 150px;
            margin-block: 11px 10px;
          }

          .wishlist-heading-copy p {
            font-size: 14px;
          }

          .wishlist-move-all {
            width: 100%;
            min-height: 47px;
            margin-top: 18px;
            border-radius: 7px;
            font-size: 14px;
          }

          .wishlist-products {
            display: flex;
            flex-direction: column;
            gap: 0;
            border-top: 1px solid var(--wishlist-line);
          }

          .wishlist-card {
            display: grid;
            grid-template-columns: 112px minmax(0, 1fr);
            min-height: 170px;
            border: 0;
            border-bottom: 1px solid var(--wishlist-line);
            border-radius: 0;
            background: transparent;
            box-shadow: none;
          }

          .wishlist-card:hover {
            transform: none;
            box-shadow: none;
          }

          .wishlist-image-link {
            align-self: stretch;
            padding: 12px 0;
          }

          .wishlist-image-wrapper {
            height: 100%;
            min-height: 145px;
            border-radius: 9px;
          }

          .wishlist-image-wrapper img {
            object-fit: cover;
            object-position: center;
          }

          .wishlist-heart-button {
            top: 15px;
            right: 1px;
            width: 30px;
            height: 30px;
            color: #a85a00;
            background: transparent;
            box-shadow: none;
          }

          .wishlist-heart-button svg {
            width: 17px;
            height: 17px;
          }

          .wishlist-card-content {
            min-height: 170px;
            padding: 15px 35px 15px 13px;
          }

          .wishlist-product-name {
            padding-right: 0;
            font-size: 16px;
            line-height: 1.25;
            -webkit-line-clamp: 2;
          }

          .wishlist-metal,
          .wishlist-size {
            margin-top: 7px;
            font-size: 11px;
          }

          .wishlist-price {
            margin-top: 10px;
            font-size: 16px;
          }

          .wishlist-card-actions {
            grid-template-columns: 44px 44px;
            justify-content: end;
            gap: 9px;
            margin-top: 11px;
          }

          .wishlist-cart-button,
          .wishlist-remove-button {
            width: 44px;
            min-height: 37px;
            border-radius: 6px;
          }

          .wishlist-cart-button span {
            display: none;
          }

          .wishlist-cart-button svg,
          .wishlist-remove-button svg {
            width: 18px;
            height: 18px;
          }

          .wishlist-benefits {
            margin-top: 18px;
            padding: 0;
            border-radius: 10px;
          }

          .wishlist-benefit {
            display: block;
            min-height: 124px;
            padding: 18px 8px;
            text-align: center;
          }

          .wishlist-benefit-icon {
            margin: 0 auto 8px;
          }

          .wishlist-benefit h3 {
            font-size: 12px;
          }

          .wishlist-benefit p {
            font-size: 10px;
            line-height: 1.45;
          }

          .wishlist-empty-state,
          .wishlist-loading-state {
            min-height: 390px;
            padding-inline: 20px;
          }

          .wishlist-empty-state h2 {
            font-size: 28px;
          }

          .wishlist-toast {
            right: 12px;
            bottom: 12px;
            left: 12px;
            justify-content: center;
            max-width: none;
          }
        }

        @media (max-width: 380px) {
          .wishlist-page {
            padding-inline: 8px;
          }

          .wishlist-card {
            grid-template-columns: 100px minmax(0, 1fr);
          }

          .wishlist-card-content {
            padding-left: 10px;
          }

          .wishlist-product-name {
            font-size: 15px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .wishlist-card,
          .wishlist-image-wrapper img,
          .wishlist-move-all,
          .wishlist-cart-button,
          .wishlist-remove-button,
          .wishlist-primary-link {
            transition: none !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Wishlist;